/**
 * Amazon Ads SP report-jobs D1 helpers — adapter-side bindings (Story 19.14).
 *
 * Mirrors the server-side implementation in
 * `apps/server/src/services/amazon-ads-report-jobs.ts` so the adapter, which
 * runs from `packages/integrations`, can issue D1 queries against the same
 * `amazon_ads_report_jobs` table without crossing the package boundary.
 *
 * The shape of the helpers (insert / findInFlightByHash / countInFlight /
 * markStatus) implements the `SpReportJobsApi` interface from
 * `sp-fetch-metrics.ts`.
 *
 * Why TWO copies (adapter + server):
 *   - The server-side copy (`apps/server/src/services/amazon-ads-report-jobs.ts`)
 *     is consumed by the reaper Cron + dry-run probe.
 *   - The adapter-side copy (this file) is consumed by `runSpFetchMetrics`,
 *     which executes inside the adapter package and cannot import from `apps/`.
 *
 *   The test contract `assertReportJobsApiContract` (sp-report-jobs-d1.test.ts)
 *   exercises both implementations through a shared in-memory D1 simulator to
 *   guard against drift.
 */

import type { SpReportJobsApi } from "./sp-fetch-metrics";

interface D1Db {
  prepare(query: string): {
    bind(...values: unknown[]): {
      run(): Promise<{ success: boolean }>;
      first<T>(col?: string): Promise<T | null>;
      all<T>(): Promise<{ results: T[] }>;
    };
  };
}

const TTL_MS = 45 * 60 * 1000;
const DEDUP_WINDOW_MS = 60 * 60 * 1000;

/**
 * Build a `SpReportJobsApi` backed by a D1 binding. Used by the adapter's
 * `fetchMetrics` orchestration in `runSpFetchMetrics`.
 */
export function buildSpReportJobsApi(db: D1Db): SpReportJobsApi {
  return {
    async insert(args) {
      const submittedAt = args.now ?? Date.now();
      const expiresAt = submittedAt + TTL_MS;
      // Story 19.16 Task 12 / AC #7 — `ad_product` column promoted to
      // MANDATORY on new rows. NULL is allowed for back-compat with rows
      // created before migration 0031, but new inserts ALWAYS pass the value.
      const r = await db
        .prepare(
          `INSERT INTO amazon_ads_report_jobs (
             id, report_id, tenant_id, integration_id, request_hash,
             status, submitted_at, expires_at, completed_at, correlation_id,
             ad_product, created_at
           ) VALUES (?, ?, ?, ?, ?, 'PENDING', ?, ?, NULL, ?, ?, ?)`
        )
        .bind(
          args.id,
          args.report_id,
          args.tenant_id,
          args.integration_id,
          args.request_hash,
          submittedAt,
          expiresAt,
          args.correlation_id,
          args.ad_product ?? null,
          submittedAt
        )
        .run();
      if (!r.success) {
        throw new Error(
          `buildSpReportJobsApi.insert: D1 reported failure for report_id=${args.report_id}`
        );
      }
    },
    async findInFlightByHash(args) {
      const now = args.now ?? Date.now();
      const cutoff = now - DEDUP_WINDOW_MS;
      const row = await db
        .prepare(
          `SELECT id, report_id, status
           FROM amazon_ads_report_jobs
           WHERE integration_id = ?
             AND request_hash = ?
             AND status IN ('PENDING','PROCESSING','COMPLETED')
             AND submitted_at > ?
           ORDER BY submitted_at DESC
           LIMIT 1`
        )
        .bind(args.integration_id, args.request_hash, cutoff)
        .first<{ id: string; report_id: string; status: string }>();
      return row;
    },
    async countInFlight(args) {
      const now = args.now ?? Date.now();
      const row = await db
        .prepare(
          `SELECT COUNT(*) AS n
           FROM amazon_ads_report_jobs
           WHERE integration_id = ?
             AND status IN ('PENDING','PROCESSING')
             AND expires_at > ?`
        )
        .bind(args.integration_id, now)
        .first<{ n: number }>();
      return row?.n ?? 0;
    },
    async markStatus(args) {
      const completedAt =
        args.completed_at !== undefined
          ? args.completed_at
          : isTerminal(args.status)
            ? Date.now()
            : null;
      await db
        .prepare(
          `UPDATE amazon_ads_report_jobs
           SET status = ?, completed_at = COALESCE(?, completed_at)
           WHERE id = ?
             AND status NOT IN ('COMPLETED','FAILED','CANCELLED','reaper_failed','cancelled_validation_probe')`
        )
        .bind(args.status, completedAt, args.id)
        .run();
      const after = await db
        .prepare(`SELECT status FROM amazon_ads_report_jobs WHERE id = ?`)
        .bind(args.id)
        .first<{ status: string }>();
      return { updated: after?.status === args.status };
    },
  };
}

function isTerminal(status: string): boolean {
  return (
    status === "COMPLETED" ||
    status === "FAILED" ||
    status === "CANCELLED" ||
    status === "reaper_failed" ||
    status === "cancelled_validation_probe"
  );
}

/**
 * Build a `SpKvApi` backed by a Cloudflare KV binding.
 */
export function buildSpKvApi(kv: {
  get(key: string, options?: { type?: string }): Promise<string | null>;
  put(key: string, value: string, options?: { expirationTtl?: number }): Promise<void>;
}): {
  get(key: string): Promise<string | null>;
  put(key: string, value: string, ttlSec: number): Promise<void>;
} {
  return {
    async get(key) {
      try {
        return await kv.get(key, { type: "text" });
      } catch {
        return null;
      }
    },
    async put(key, value, ttlSec) {
      try {
        await kv.put(key, value, { expirationTtl: ttlSec });
      } catch {
        // fail-open
      }
    },
  };
}
